import { useLayoutEffect, useRef, useState } from 'react';
import { EASE_OUT, prefersReducedMotion } from '@/lib/motion';

// A value that changed under the reader's hands: the old one fades up and out
// as the new one fades up into place, over SWAP_MS. Seen once per edit, so it
// is short and moves only RISE_PX.
const SWAP_MS = 220;
const RISE_PX = 6;

/**
 * Whether a swap can play at all: no Web Animations API (jsdom, an old
 * engine), or a reader who asked for less motion, gets a plain replacement.
 */
export function canSwap() {
    return typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function' && !prefersReducedMotion();
}

/**
 * Plays a ValueRow's `swap`, `{ from, onEnd }`: the layer the row draws for
 * the old value (`outgoingRef`) leaves while the one for the new value
 * (`incomingRef`) arrives, and `onEnd()` is called once the old one is done
 * and can be removed. Without a swap nothing runs.
 *
 * `playing` says the row should draw both layers. A swap that cannot play
 * (see canSwap) ends at once instead, with the old layer never drawn.
 * `layered` stays true once a row has swapped, so its value is not mounted
 * again, with a flash for an image, when the old layer goes.
 */
export default function useValueSwap(swap) {
    const outgoingRef = useRef(null);
    const incomingRef = useRef(null);
    const playing = swap != null && canSwap();

    const [layered, setLayered] = useState(false);
    if (playing && !layered) setLayered(true);

    useLayoutEffect(() => {
        if (!swap) return undefined;

        const outgoing = playing ? outgoingRef.current : null;
        const incoming = incomingRef.current;
        if (typeof outgoing?.animate !== 'function' || !incoming) {
            swap.onEnd?.();
            return undefined;
        }

        // The old layer holds its last frame until React removes it, so it
        // cannot flash back to full between the end and the removal.
        const leaving = outgoing.animate(
            [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: `translateY(-${RISE_PX}px)` }],
            { duration: SWAP_MS, easing: EASE_OUT, fill: 'forwards' },
        );
        const arriving = incoming.animate(
            [{ opacity: 0, transform: `translateY(${RISE_PX}px)` }, { opacity: 1, transform: 'none' }],
            { duration: SWAP_MS, easing: EASE_OUT, fill: 'backwards' },
        );
        leaving.onfinish = () => swap.onEnd?.();

        return () => {
            leaving.cancel();
            arriving.cancel();
        };
    }, [swap, playing]);

    return { playing, layered, outgoingRef, incomingRef };
}
